import React, { useEffect, useContext, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Checkbox,
  IconButton,
  Divider,
} from "@mui/material";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";
import { useForm, FormProvider } from "react-hook-form";
import { RHFSelect } from "../RFHSelect/RFHSelect";
import { Edit, Delete, Add, Save } from "@mui/icons-material";

export const PhaseDialog = ({
  open,
  onClose,
  phaseData,
  onCopy,
  onEdit,
  onCurrentEdit,
  onDelete,
  actionType,
}) => {
  const methods = useForm({
    name: phaseData?.name,
    description: phaseData?.description,
    repeattime: phaseData?.repeattime,
    duration: phaseData?.duration,
    active: phaseData?.active,
  });
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;

  const { darkMode } = useContext(ThemeContext);

  const reliefPhaseIds = new Set([4, 5, 1, 45]);
  const correctivePhaseIds = new Set([10, 2, 46, 6]);
  const wellnessPhaseIds = new Set([3, 7, 47]);

  const reliefPhaseNames = new Set([
    "Relief",
    "Acute",
    "Intensive",
    "Crisis Care Phase",
  ]);
  const correctivePhaseNames = new Set([
    "Corrective",
    "Rehabilitative",
    "Critical Transition Phase",
    "Recovery",
  ]);
  const wellnessPhaseNames = new Set([
    "Wellness",
    "Maintenance",
    "Lifestyle Care Phase",
  ]);

  const defaultGoals = [
    [
      { id: 1, title: "Reduce Pain", checked: 1 },
      { id: 2, title: "Reduce Inflammation", checked: 1 },
      { id: 3, title: "Improve Joint Function", checked: 1 },
      { id: 4, title: "Improve Posture", checked: 1 },
    ],
    [
      { id: 5, title: "Reduce / Eliminate Pain", checked: 1 },
      { id: 6, title: "Increase Range of Motion", checked: 1 },
      { id: 7, title: "Improve Joint Function", checked: 1 },
      { id: 8, title: "Improve Posture", checked: 1 },
      { id: 9, title: "Retrain Muscles", checked: 1 },
      { id: 10, title: "Strengthen Ligaments", checked: 1 },
    ],
    [
      { id: 11, title: "Maintain Structural Correction", checked: 1 },
      { id: 12, title: "Maintain Normal Range of Motion", checked: 1 },
      { id: 13, title: "Preserve Joint Health", checked: 1 },
      { id: 14, title: "Prevent Spinal Degeneration", checked: 1 },
    ],
  ];

  const [goals, setGoals] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [newGoal, setNewGoal] = useState("");

  useEffect(() => {
    if (phaseData) {
      setValue("name", phaseData?.name);
      setValue("description", phaseData?.description);
      setValue("repeattime", phaseData?.repeattime);
      setValue("duration", phaseData?.duration);
      setValue("active", phaseData?.active);

      if (phaseData.goals) {
        setGoals(phaseData.goals);
      } else {
        const { phaseid, name } = phaseData;
        if (phaseid) {
          if (reliefPhaseIds.has(phaseid)) {
            setGoals(defaultGoals[0]);
          } else if (correctivePhaseIds.has(phaseid)) {
            setGoals(defaultGoals[1]);
          } else if (wellnessPhaseIds.has(phaseid)) {
            setGoals(defaultGoals[2]);
          } else {
            setGoals([]);
          }
        } else if (name) {
          if (reliefPhaseNames.has(name)) {
            setGoals(defaultGoals[0]);
          } else if (correctivePhaseNames.has(name)) {
            setGoals(defaultGoals[1]);
          } else if (wellnessPhaseNames.has(name)) {
            setGoals(defaultGoals[2]);
          } else {
            setGoals([]);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseData, setValue]);

  useEffect(() => {
    setNewGoal("");
  }, [open]);

  // useEffect(() => {
  //   if (actionType === "add") {
  //     setValue("repeattime", "1 time per month");
  //     setValue("duration", "1 week");
  //     setValue("active", "Passive Care");
  //   }
  // }, [open, actionType, setValue]);

  // const onDeleteClicked = () => {
  //   onDelete(phaseData.id);
  // };

  const onSave = (data) => {
    if (goals.length) {
      const phase = {
        ...((actionType === "edit" || actionType === "current-edit") && {
          id: phaseData.id,
        }), // Add 'id' only for editing
        phaseid: phaseData.phaseid,
        name: data.name,
        description: data.description,
        repeattime: data.repeattime,
        duration: data.duration,
        active: data.active,
        goals: goals,
      };
      // if (actionType === "edit") onEdit(phase);
      // else if (actionType === "add") onCopy(phase);
      // else if (actionType === "current-edit") onCurrentEdit(phase);
      if (actionType === "current-edit") onCurrentEdit(phase);

      onClose(); // Close the dialog}
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ fontSize: "30px", pb: 0 }}>
        <>
          {/* {actionType === "add" && "Create New Phase"} */}
          {(actionType === "edit" || actionType === "current-edit") &&
            "Edit Phase"}
        </>
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSave)}>
          <DialogContent sx={{ pt: 0 }}>
            <FormControl fullWidth margin="normal">
              <TextField
                label="Phase Name"
                placeholder="Enter Phase Name"
                {...register("name", {
                  required: "Phase Name is required",
                })}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <TextField
                label="Phase Description"
                multiline
                rows={4}
                placeholder="Enter Phase Description"
                {...register("description", {
                  required: "Phase Description is required",
                })}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            </FormControl>

            {/* Frequency Dropdown */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Select the frequency of the treatment</InputLabel>
              <RHFSelect
                name="repeattime"
                error={!!errors.repeattime}
                helperText={errors.repeattime?.message}
              >
                <MenuItem value="1 time per month">1 time per month</MenuItem>
                <MenuItem value="2 times per month">2 times per month</MenuItem>
                <MenuItem value="3 times per month">3 times per month</MenuItem>
                <MenuItem value="1 time per week">1 time per week</MenuItem>
                <MenuItem value="2 times per week">2 times per week</MenuItem>
                <MenuItem value="3 times per week">3 times per week</MenuItem>
                <MenuItem value="4 times per week">4 times per week</MenuItem>
                <MenuItem value="5 times per week">5 times per week</MenuItem>
              </RHFSelect>
            </FormControl>

            {/* Duration Dropdown */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Select the duration of the treatment</InputLabel>
              <RHFSelect
                name="duration"
                error={!!errors.duration}
                helperText={errors.duration?.message}
              >
                <MenuItem value="1 week">1 week</MenuItem>
                <MenuItem value="2 weeks">2 weeks</MenuItem>
                <MenuItem value="3 weeks">3 weeks</MenuItem>
                <MenuItem value="4 weeks">4 weeks</MenuItem>
                <MenuItem value="5 weeks">5 weeks</MenuItem>
                <MenuItem value="6 weeks">6 weeks</MenuItem>
                <MenuItem value="7 weeks">7 weeks</MenuItem>
                <MenuItem value="8 weeks">8 weeks</MenuItem>
                <MenuItem value="1 month">1 month</MenuItem>
                <MenuItem value="2 months">2 months</MenuItem>
                <MenuItem value="3 months">3 months</MenuItem>
                <MenuItem value="4 months">4 months</MenuItem>
                <MenuItem value="5 months">5 months</MenuItem>
                <MenuItem value="6 months">6 months</MenuItem>
              </RHFSelect>
            </FormControl>

            {/* Active Dropdown */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Select Active or Passive Care</InputLabel>
              <RHFSelect
                name="active"
                error={!!errors.active}
                helperText={errors.active?.message}
              >
                <MenuItem value="Passive Care">Passive Care</MenuItem>
                <MenuItem value="Active Care">Active Care</MenuItem>
                <MenuItem value="Both Passive and Active Care">
                  Both Passive and Active Care
                </MenuItem>
              </RHFSelect>
            </FormControl>

            {/* Goals Section */}
            <Box>
              <FormControl fullWidth margin="normal">
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    color: darkMode
                      ? getColors.thumbnailTextDark
                      : getColors.secondaryTextLight,
                  }}
                >
                  Select Goals
                </Typography>
              </FormControl>

              {/* Add New Goal */}
              <Box display="flex" alignItems="center">
                <FormControl fullWidth margin="normal">
                  <TextField
                    placeholder="Add new goal"
                    value={newGoal}
                    onChange={(e) => setNewGoal(e.target.value)}
                    size="small"
                    sx={{ flex: 1, mr: 1 }}
                  />
                </FormControl>
                <IconButton
                  onClick={() => {
                    if (!newGoal.trim()) return;
                    setGoals((prev) => [
                      ...prev,
                      {
                        id: Date.now(),
                        title: newGoal.trim(),
                        checked: 1,
                      },
                    ]);
                    setNewGoal("");
                  }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>

              {goals.length === 0 && (
                <Typography sx={{ color: "#f44336", fontSize: "12px" }} ml={2}>
                  You should add at least 1 goal
                </Typography>
              )}

              {goals.length > 0 && <Divider sx={{ my: 1 }} />}

              <Box>
                {goals.map((goal) => (
                  <Box
                    key={goal.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    py={1}
                    borderBottom="1px solid #e0e0e0"
                  >
                    {/* Left Side: Checkbox and Title */}
                    <Box display="flex" alignItems="center" flex={1}>
                      <Checkbox
                        edge="start"
                        checked={goal.checked === 1}
                        onChange={() =>
                          setGoals((prev) =>
                            prev.map((g) =>
                              g.id === goal.id
                                ? { ...g, checked: g.checked === 1 ? 0 : 1 }
                                : g
                            )
                          )
                        }
                      />
                      {editingId === goal.id ? (
                        <TextField
                          value={goal.title}
                          onChange={(e) =>
                            setGoals((prev) =>
                              prev.map((g) =>
                                g.id === goal.id
                                  ? { ...g, title: e.target.value }
                                  : g
                              )
                            )
                          }
                          onBlur={() => setEditingId(null)}
                          size="small"
                          sx={{ flex: 1, mr: 1 }}
                          autoFocus
                        />
                      ) : (
                        <Typography
                          variant="body1"
                          onClick={() =>
                            setGoals((prev) =>
                              prev.map((g) =>
                                g.id === goal.id
                                  ? { ...g, checked: g.checked === 1 ? 0 : 1 }
                                  : g
                              )
                            )
                          }
                          sx={{ cursor: "pointer", userSelect: "none" }}
                        >
                          {goal.title}
                        </Typography>
                      )}
                    </Box>

                    {/* Right Side: Edit/Delete Buttons */}
                    <Box display="flex" alignItems="center">
                      {editingId === goal.id ? (
                        <IconButton onClick={() => setEditingId(null)}>
                          <Save fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton onClick={() => setEditingId(goal.id)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        onClick={() =>
                          setGoals((prev) =>
                            prev.filter((g) => g.id !== goal.id)
                          )
                        }
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                color: darkMode
                  ? getColors.appBarBgLight
                  : getColors.appBarBgDark,
                px: 4,
                py: "11px",
                textAlign: "center",
                "&: hover": {
                  background: "none",
                  color: getColors.primaryMain,
                },
              }}
              onClick={onClose}
            >
              Cancel
            </Button>
            {/* {actionType === "edit" && (
              <Button
                variant="contained"
                sx={{
                  borderRadius: "5px",
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 500,
                  px: 5,
                  py: "11px",
                  mr: 1,
                  textAlign: "center",
                }}
                onClick={onDeleteClicked}
              >
                Delete
              </Button>
            )} */}
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                px: 5,
                py: "11px",
                textAlign: "center",
              }}
            >
              {actionType === "add" ? "Add" : "Save"}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
